// src/layouts/publish-here/components/PublishSteps/PdfReviewStage.jsx
import React, { useEffect, useState, useRef } from "react";
import { Box, Typography, Button, Card, Alert, LinearProgress } from "@mui/material";
import FlowActions from "components/FlowActions";
import HTMLFlipBook from "react-pageflip";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";

// Mesmo worker que no pdfUtils
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

export default function PdfReviewStage({ file, pdfInfo, onRefazer, onProsseguir }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(0);
  const [pages, setPages] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const bookRef = useRef(null);

  useEffect(() => {
    let active = true;
    let pdf;
    setLoading(true);
    setError("");
    setPages([]);
    setProgress(0);
    (async () => {
      try {
        if (!file) throw new Error("Nenhum PDF selecionado.");
        const buffer = await file.arrayBuffer();
        pdf = await pdfjsLib.getDocument({ data: buffer }).promise;
        if (!active) return;
        const imgs = await convertPdfToImages(
          pdf,
          () => active,
          (value) => {
            if (active) setProgress(value);
          }
        );
        if (active) {
          setPages(imgs);
          setCurrentPage(0);
        }
      } catch (_) {
        if (active)
          setError("Não foi possível preparar a revisão do miolo. Selecione o PDF novamente.");
      } finally {
        if (active) setLoading(false);
        if (pdf) await pdf.destroy();
      }
    })();
    return () => {
      active = false;
    };
  }, [file]);

  const next = () => bookRef.current?.pageFlip().flipNext();
  const prev = () => bookRef.current?.pageFlip().flipPrev();

  return (
    <Box mt={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {loading && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" mb={1}>
            Preparando a revisão do miolo… {progress}%
          </Typography>
          <LinearProgress variant="determinate" value={progress} />
        </Box>
      )}
      <Box
        className="manuscript-review"
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "320px minmax(0, 1fr)" },
          gap: 3,
        }}
      >
        {/* LADO ESQUERDO – info do miolo + botões */}
        <Card
          sx={{
            padding: 3,
            alignSelf: "start",
            border: "1px solid #e2e9e4",
            boxShadow: "none",
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            Confira o miolo do seu livro
          </Typography>

          <Box sx={{ mt: 2, p: 2, background: "#eef6ff", borderRadius: 2 }}>
            <Typography fontWeight="bold">Configurações do seu arquivo</Typography>
            <Typography>
              Tamanho:{" "}
              <b>
                {pdfInfo?.widthCm} × {pdfInfo?.heightCm} cm
              </b>
            </Typography>
            <Typography>
              Páginas: <b>{pdfInfo?.pages}</b>
            </Typography>
          </Box>

          <Box sx={{ mt: 2, p: 2, border: "1px solid #ddd", borderRadius: 2 }}>
            <Typography fontWeight="bold" mb={1}>
              O que você deve conferir:
            </Typography>
            <ul>
              <li>Folheie até o final.</li>
              <li>Veja se o conteúdo não está muito perto da margem.</li>
              <li>Se precisar, corrija e envie um novo arquivo.</li>
            </ul>
          </Box>
        </Card>

        {/* LADO DIREITO – flipbook */}
        <Box
          sx={{
            minWidth: 0,
            overflow: "hidden",
            bgcolor: "#eaf0ec",
            border: "1px solid #dfe8e2",
            p: { xs: 1, md: 3 },
            borderRadius: 3,
          }}
          textAlign="center"
          position="relative"
        >
          {pages.length > 0 && (
            <HTMLFlipBook
              width={480}
              height={650}
              size="stretch"
              minWidth={180}
              maxWidth={600}
              showCover={true}
              ref={bookRef}
              onFlip={(e) => setCurrentPage(e.data)}
              style={{ margin: "auto" }}
            >
              {pages.map((url, i) => (
                <div key={i}>
                  <img
                    alt={`Página ${i + 1} do miolo`}
                    src={url}
                    style={{ width: "100%", height: "100%", objectFit: "contain" }}
                  />
                </div>
              ))}
            </HTMLFlipBook>
          )}

          {/* Seta esquerda */}
          {currentPage > 0 && (
            <Button
              onClick={prev}
              sx={{
                position: "absolute",
                top: "50%",
                left: 10,
                transform: "translateY(-50%)",
                background: "#000",
                color: "#fff",
                borderRadius: "50%",
                minWidth: 40,
                minHeight: 40,
              }}
            >
              ‹
            </Button>
          )}

          {/* Seta direita */}
          {currentPage < pages.length - 1 && (
            <Button
              onClick={next}
              sx={{
                position: "absolute",
                top: "50%",
                right: 10,
                transform: "translateY(-50%)",
                background: "#000",
                color: "#fff",
                borderRadius: "50%",
                minWidth: 40,
                minHeight: 40,
              }}
            >
              ›
            </Button>
          )}

          {/* Slider de página */}
          {pages.length > 0 && (
            <Box mt={3}>
              <input
                type="range"
                min={0}
                max={pages.length - 1}
                value={currentPage}
                onChange={(e) => {
                  const p = Number(e.target.value);
                  setCurrentPage(p);
                  bookRef.current?.pageFlip().flip(p);
                }}
                style={{ width: "80%" }}
              />
            </Box>
          )}
        </Box>
      </Box>
      <FlowActions
        onBack={onRefazer}
        backLabel="Trocar arquivo"
        onContinue={onProsseguir}
        continueLabel="Continuar para a capa"
        disabled={loading || Boolean(error) || !pages.length}
        hint={
          loading
            ? "Aguarde o carregamento da revisão."
            : "Confira as páginas e as margens antes de continuar."
        }
      />
    </Box>
  );
}

async function convertPdfToImages(pdf, isActive, onProgress) {
  const out = [];

  for (let i = 1; i <= pdf.numPages; i++) {
    if (!isActive()) break;
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 1.5 });

    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    canvas.width = viewport.width;
    canvas.height = viewport.height;

    await page.render({ canvasContext: ctx, viewport }).promise;

    out.push(canvas.toDataURL("image/jpeg", 0.9));
    onProgress(Math.round((i / pdf.numPages) * 100));
  }

  return out;
}
